/**
 * Animation looks (ENG-2347): Lottie animations uploaded in the editor. Each
 * fills the screen on black, and each frame is picked from the look's clock,
 * so every phone shows the same frame (or, with "random" timing, its own point
 * in the loop).
 *
 * lottie-web draws each animation on its own offscreen canvas, which is then
 * copied onto the look's canvas, so it never touches that canvas's state and
 * several previews of one file don't draw over each other.
 */
import { AnimationLook } from ".";

type Ctx = CanvasRenderingContext2D;

/** The parts of lottie-web's canvas player used here. */
interface LottiePlayer {
  loadAnimation: (params: {
    renderer: "canvas";
    loop: boolean;
    autoplay: boolean;
    animationData: unknown;
    rendererSettings: {
      context: CanvasRenderingContext2D;
      clearCanvas: boolean;
      preserveAspectRatio: string;
    };
  }) => LottieAnimation;
}

interface LottieAnimation {
  isLoaded: boolean;
  totalFrames: number;
  frameRate: number;
  goToAndStop: (value: number, isFrame?: boolean) => void;
  /** "DOMLoaded" fires once it can draw, embedded images included. */
  addEventListener: (name: "DOMLoaded", callback: () => void) => void;
  destroy: () => void;
}

/** Files bigger than this would be slow to load on a phone at a show. */
export const MAX_ANIMATION_BYTES = 2 * 1024 * 1024;
/** A loop longer than this is more likely a mistake than a light show look. */
const MAX_ANIMATION_SECONDS = 120;
/** How long to wait before fetching a file that failed again. */
const RETRY_MS = 5000;
/**
 * Idle players kept around; each holds a canvas the size of its preview.
 * Players drawn in the last IDLE_MS are never evicted, however many there
 * are, so previews on screen don't push each other out.
 */
const MAX_PLAYERS = 12;
const IDLE_MS = 2000;

// --- lottie-web --------------------------------------------------------------

let loadLottie: (() => Promise<unknown>) | undefined;
let lottie: LottiePlayer | undefined;
let lottiePromise: Promise<LottiePlayer> | undefined;

/**
 * How this app loads lottie-web. Register it once with a dynamic import of the
 * light canvas build, which can't run expressions (code in the file), e.g.
 * `setLottieLoader(() => import("lottie-web/build/player/lottie_light_canvas").then((m) => m.default))`,
 * so the app's bundler keeps it in its own chunk. (lottie-web's types insist
 * on a container element, which its canvas player doesn't need when it's
 * given a canvas, so the player is taken as it comes.)
 */
export const setLottieLoader = (loader: () => Promise<unknown>) => {
  loadLottie = loader;
};

/** Loads lottie-web once; undefined if the app hasn't registered a loader. */
const loadLottieOnce = () => {
  if (!lottiePromise && loadLottie) {
    lottiePromise = loadLottie().then(
      (player) => (lottie = player as LottiePlayer),
      (error) => {
        // Try again later, e.g. after a dropped connection.
        lottiePromise = undefined;
        throw error;
      },
    );
  }
  return lottiePromise;
};

/** lottie-web if it's in; otherwise starts loading it. */
const getLottie = () => {
  if (!lottie) loadLottieOnce()?.catch(() => {});
  return lottie ?? null;
};

// --- files -------------------------------------------------------------------

interface AnimationFile {
  /** Kept as text: lottie-web changes the data it's given, so each player parses its own copy. */
  text: string;
  /** Seconds in one loop. */
  duration: number;
}

const files = new Map<string, AnimationFile>();
const fileRequests = new Map<string, Promise<AnimationFile>>();
const failedAt = new Map<string, number>();

/** Fetches a file once (again after RETRY_MS if it failed); undefined while waiting to retry. */
const loadFile = (src: string) => {
  const pending = fileRequests.get(src);
  if (pending) return pending;
  if (Date.now() - (failedAt.get(src) ?? -Infinity) < RETRY_MS)
    return undefined;
  const request = fetch(src)
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.text();
    })
    .then((text) => {
      const { fr, ip, op } = JSON.parse(text);
      const file = { text, duration: (op - ip) / fr || 0 };
      files.set(src, file);
      return file;
    })
    .catch((error) => {
      fileRequests.delete(src);
      failedAt.set(src, Date.now());
      throw error;
    });
  fileRequests.set(src, request);
  return request;
};

/** The file if it's in; otherwise starts fetching it. */
const getFile = (src: string) => {
  if (!files.has(src)) loadFile(src)?.catch(() => {});
  return files.get(src) ?? null;
};

// --- players -----------------------------------------------------------------

interface Player {
  animation: LottieAnimation;
  canvas: HTMLCanvasElement;
  /** When it was last drawn (ms). */
  drawnAt: number;
}

/** By file and size; least recently drawn first. */
const players = new Map<string, Player>();
/** Files that have finished loading (embedded images included). */
const ready = new Set<string>();
/** Files being prepared for `ready` by preloading. */
const preparing = new Set<string>();

/** A player drawing `file` on `canvas`, filling it and cropping the edges. */
const createAnimation = (
  player: LottiePlayer,
  file: AnimationFile,
  canvas: HTMLCanvasElement,
) => {
  const context = canvas.getContext("2d");
  if (!context) return null;
  return player.loadAnimation({
    renderer: "canvas",
    loop: false,
    autoplay: false,
    animationData: JSON.parse(file.text),
    rendererSettings: {
      context,
      clearCanvas: true,
      preserveAspectRatio: "xMidYMid slice",
    },
  });
};

/** Destroys the least recently drawn idle players beyond MAX_PLAYERS. */
const evictIdlePlayers = (now: number) => {
  for (const [key, { animation, drawnAt }] of players) {
    if (players.size <= MAX_PLAYERS) return;
    // Oldest first; once one was drawn recently, so were all after it.
    if (now - drawnAt < IDLE_MS) return;
    animation.destroy();
    players.delete(key);
  }
};

const getPlayer = (look: AnimationLook, width: number, height: number) => {
  const key = `${width}x${height} ${look.src}`;
  const now = Date.now();
  const existing = players.get(key);
  if (existing) {
    players.delete(key);
    players.set(key, { ...existing, drawnAt: now });
    // Also here: players created in a burst can go idle while only cached
    // ones are drawn, and would otherwise keep their canvases for good.
    evictIdlePlayers(now);
    return existing;
  }

  const player = getLottie();
  const file = getFile(look.src);
  if (!player || !file) return null;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const animation = createAnimation(player, file, canvas);
  if (!animation) return null;
  const created = { animation, canvas, drawnAt: now };
  players.set(key, created);
  evictIdlePlayers(now);
  return created;
};

/**
 * Loads lottie-web, the file and its embedded images, then marks the file
 * ready, without anything having to draw it: a 1x1 player loads the images
 * and is dropped once they're in.
 */
const prepare = (src: string) => {
  if (ready.has(src) || preparing.has(src)) return;
  // No loader registered yet: wait for one before fetching anything, so no
  // request is left without a handler.
  const lottieRequest = loadLottieOnce();
  if (!lottieRequest) return;
  const fileRequest = loadFile(src);
  if (!fileRequest) return;
  preparing.add(src);
  Promise.all([lottieRequest, fileRequest])
    .then(([player, file]) => {
      const canvas = document.createElement("canvas");
      canvas.width = 1;
      canvas.height = 1;
      const probe = createAnimation(player, file, canvas);
      if (!probe) throw new Error("No 2D canvas");
      const done = () => {
        ready.add(src);
        preparing.delete(src);
        probe.destroy();
      };
      if (probe.isLoaded) done();
      else probe.addEventListener("DOMLoaded", done);
    })
    .catch(() => {
      // Tried again by the next preload or readiness check.
      preparing.delete(src);
    });
};

// --- public API --------------------------------------------------------------

export const renderAnimation = (
  ctx: Ctx,
  w: number,
  h: number,
  t: number,
  look: AnimationLook,
  seed: number,
) => {
  ctx.fillStyle = "#000000";
  ctx.fillRect(0, 0, w, h);
  if (!look.src) return;

  // Draw at the canvas's own resolution (its device pixel ratio included).
  const scale = ctx.getTransform().a || 1;
  const player = getPlayer(
    look,
    Math.max(1, Math.round(w * scale)),
    Math.max(1, Math.round(h * scale)),
  );
  if (!player?.animation.isLoaded) return;
  ready.add(look.src);

  const { totalFrames, frameRate } = player.animation;
  if (!(totalFrames > 0 && frameRate > 0)) return;
  const offset = look.timing === "random" ? seed * totalFrames : 0;
  // t can be just under 0 at a look's first frame; keep the frame in range.
  const position = (t * frameRate + offset) % totalFrames;
  player.animation.goToAndStop((position + totalFrames) % totalFrames, true);
  ctx.drawImage(player.canvas, 0, 0, w, h);
};

/**
 * Loads lottie-web, the file and its embedded images, so the look shows as it
 * goes live; isAnimationReady says when they're in.
 */
export const preloadAnimation = (look: AnimationLook) => {
  if (look.src) prepare(look.src);
};

/**
 * Whether the animation is loaded, embedded images included. Starts loading
 * it if nothing has, so waiting on this always finishes.
 */
export const isAnimationReady = (look: AnimationLook) => {
  if (!look.src) return true;
  prepare(look.src);
  return ready.has(look.src);
};

/**
 * When to draw a still: at the file's "still" marker. With random timing a
 * phone's point in the loop is shifted by its seed, so the shift is undone.
 */
export const getAnimationStillTime = (look: AnimationLook, seed: number) => {
  const duration =
    look.timing === "random" ? (files.get(look.src)?.duration ?? 0) : 0;
  return (look.stillTime ?? 0) - seed * duration;
};

// --- checking uploads --------------------------------------------------------

export type AnimationFileCheck =
  | {
      ok: true;
      colors: string[];
      stillTime?: number;
      /** Seconds in one loop. */
      duration: number;
    }
  | { ok: false; error: string };

const fail = (error: string): AnimationFileCheck => ({ ok: false, error });

type JsonObject = Record<string, unknown>;

const isObject = (value: unknown): value is JsonObject =>
  !!value && typeof value === "object" && !Array.isArray(value);

const asArray = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : [];

const toHex = (rgb: unknown) => {
  if (!Array.isArray(rgb) || rgb.length < 3) return undefined;
  const channels = rgb.slice(0, 3);
  if (!channels.every((c) => typeof c === "number")) return undefined;
  return `#${channels
    .map((c: number) =>
      Math.round(Math.min(1, Math.max(0, c)) * 255)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;
};

/**
 * Checks an uploaded Lottie file for what the light show can play: nothing
 * that needs code (expressions), fonts (text layers) or other files (linked
 * images), at a size phones can load at a show. Also reads its main colors
 * and the "still" marker. Flashing isn't checked here; looks are reviewed
 * against MAX_FLASH_HZ before a show.
 */
export const checkAnimationFile = (text: string): AnimationFileCheck => {
  // UTF-8 bytes, as stored; never fewer than the string's length, so long
  // strings fail before being encoded.
  if (
    text.length > MAX_ANIMATION_BYTES ||
    new TextEncoder().encode(text).length > MAX_ANIMATION_BYTES
  ) {
    return fail(
      `The file is too large; keep it under ${MAX_ANIMATION_BYTES / 1024 / 1024} MB.`,
    );
  }
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return fail("This file isn't valid JSON.");
  }
  if (!isObject(data)) return fail("This isn't a Lottie animation.");
  const { fr, ip, op, w, h, layers } = data;
  const finite = (n: unknown): n is number =>
    typeof n === "number" && Number.isFinite(n);
  if (
    !finite(fr) ||
    !finite(ip) ||
    !finite(op) ||
    !finite(w) ||
    !finite(h) ||
    !Array.isArray(layers)
  ) {
    return fail("This isn't a Lottie animation.");
  }
  if (!(fr > 0 && w > 0 && h > 0 && op > ip)) {
    return fail(
      "This animation can't play: it needs a frame rate, a size, and an end after its start.",
    );
  }
  const duration = (op - ip) / fr;
  if (!(duration > 0) || duration > MAX_ANIMATION_SECONDS) {
    return fail(
      `The animation should loop in under ${MAX_ANIMATION_SECONDS} seconds.`,
    );
  }

  const colorCounts = new Map<string, number>();
  const countColor = (rgb: unknown) => {
    const hex = toHex(rgb);
    if (hex) colorCounts.set(hex, (colorCounts.get(hex) ?? 0) + 1);
  };
  let problem: string | undefined;
  const visit = (value: unknown): void => {
    if (problem) return;
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (!isObject(value)) return;
    const node = value;
    if (typeof node.x === "string") {
      problem =
        "This file uses expressions, which can't play here. Convert them to keyframes before exporting.";
      return;
    }
    if (node.ty === 5 && "t" in node) {
      problem =
        "This file has text layers. Convert text to shapes before exporting.";
      return;
    }
    // Fills and strokes: a static color, or a keyframed one.
    if ((node.ty === "fl" || node.ty === "st") && isObject(node.c)) {
      const k = asArray(node.c.k);
      if (typeof k[0] === "number") countColor(k);
      else k.forEach((key) => isObject(key) && countColor(key.s));
    }
    Object.values(node).forEach(visit);
  };
  visit(layers);
  for (const asset of asArray(data.assets)) {
    if (!isObject(asset)) continue;
    // Images are assets with a path; embedded ones are flagged (e: 1) and
    // carry their data as a data URI rather than a link.
    if (
      typeof asset.p === "string" &&
      (asset.e !== 1 || !asset.p.startsWith("data:image/"))
    ) {
      problem ??=
        "This file links to images. Embed images in the file when exporting.";
    }
    visit(asset.layers);
  }
  if (problem) return fail(problem);

  const still = asArray(data.markers).find(
    (m): m is { tm: number } =>
      isObject(m) && m.cm === "still" && typeof m.tm === "number",
  );
  const colors = [...colorCounts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([hex]) => hex);
  return {
    ok: true,
    colors,
    stillTime: still ? (still.tm - ip) / fr : undefined,
    duration,
  };
};
